import { StructuredJsonFieldDef } from '../../shared/design-system/structured-json-form.model';

export const UW_FORM_ANSWER_FIELD_LABELS: Record<string, string> = {
  vehicleUseType: 'insurance.uwCaseDetail.snapshot.vehicleUseType',
  quoteId: 'insurance.uwCaseDetail.snapshot.quoteReference'
};

export const INSURED_PARTY_ITEM_FIELDS: StructuredJsonFieldDef[] = [
  { key: 'partyId', type: 'party', labelKey: 'insurance.uwCaseDetail.snapshot.party', required: true }
];

export const BENEFICIARY_ITEM_FIELDS: StructuredJsonFieldDef[] = [
  { key: 'partyId', type: 'party', labelKey: 'insurance.uwCaseDetail.snapshot.party', required: true }
];

export const RISK_OBJECT_ITEM_FIELDS: StructuredJsonFieldDef[] = [
  { key: 'zoneCode', type: 'string', labelKey: 'insurance.uwCaseDetail.snapshot.zoneCode' },
  { key: 'description', type: 'string', labelKey: 'insurance.uwCaseDetail.snapshot.description' }
];

export const SELECTED_COVERAGE_ITEM_FIELDS: StructuredJsonFieldDef[] = [
  { key: 'coverageCode', type: 'string', labelKey: 'insurance.uwCaseDetail.snapshot.coverageCode', required: true },
  { key: 'sumInsured', type: 'number', labelKey: 'insurance.uwCaseDetail.snapshot.sumInsured' },
  { key: 'selected', type: 'boolean', labelKey: 'insurance.uwCaseDetail.snapshot.selected' }
];

export function normalizeSelectedCoverageItem(item: JsonArrayItem): JsonArrayItem {
  return {
    coverageCode: item['coverageCode'] ?? item['CoverageCode'] ?? '',
    sumInsured: item['sumInsured'] ?? item['SumInsured'] ?? null,
    selected: item['selected'] ?? item['Selected'] ?? null
  };
}

export const AI_EXPLANATION_SCALAR_FIELDS: StructuredJsonFieldDef[] = [
  { key: 'advisoryOnly', type: 'boolean', labelKey: 'insurance.ai.explanation.advisoryOnly' },
  { key: 'alert', type: 'boolean', labelKey: 'insurance.ai.explanation.alert' },
  { key: 'autoBlock', type: 'boolean', labelKey: 'insurance.ai.explanation.autoBlock' },
  { key: 'threshold', type: 'number', labelKey: 'insurance.ai.explanation.threshold' },
  { key: 'action', type: 'string', labelKey: 'insurance.ai.explanation.action' },
  { key: 'lineOfBusiness', type: 'string', labelKey: 'insurance.ai.explanation.lineOfBusiness' },
  { key: 'premiumAmount', type: 'number', labelKey: 'insurance.ai.explanation.premiumAmount' },
  { key: 'daysToExpiry', type: 'number', labelKey: 'insurance.ai.explanation.daysToExpiry' },
  { key: 'policyStatus', type: 'string', labelKey: 'insurance.ai.explanation.policyStatus' },
  { key: 'suggestedDiscountPercent', type: 'number', labelKey: 'insurance.ai.explanation.suggestedDiscountPercent' },
  { key: 'minPremium', type: 'number', labelKey: 'insurance.ai.explanation.minPremium' },
  { key: 'maxDiscountPercent', type: 'number', labelKey: 'insurance.ai.explanation.maxDiscountPercent' },
  { key: 'guardrailApplied', type: 'boolean', labelKey: 'insurance.ai.explanation.guardrailApplied' },
  { key: 'answer', type: 'textarea', labelKey: 'insurance.ai.explanation.answer' }
];

export const AI_ENTITY_TYPE_OPTIONS = [
  { value: 'InsurancePolicy', labelKey: 'insurance.ai.entityTypes.insurancePolicy' },
  { value: 'InsuranceClaim', labelKey: 'insurance.ai.entityTypes.insuranceClaim' },
  { value: 'InsuranceQuote', labelKey: 'insurance.ai.entityTypes.insuranceQuote' },
  { value: 'InsuranceUwCase', labelKey: 'insurance.ai.entityTypes.insuranceUwCase' }
];

export const AI_RECOMMENDATION_TYPE_OPTIONS = [
  { value: 'RiskScore', labelKey: 'insurance.ai.recommendationTypes.riskScore' },
  { value: 'CoverageRecommendation', labelKey: 'insurance.ai.recommendationTypes.coverageRecommendation' },
  { value: 'FraudDetection', labelKey: 'insurance.ai.recommendationTypes.fraudDetection' },
  { value: 'RenewalPrediction', labelKey: 'insurance.ai.recommendationTypes.renewalPrediction' },
  { value: 'RiskBasedPricingHint', labelKey: 'insurance.ai.recommendationTypes.riskBasedPricingHint' },
  { value: 'CopilotSummary', labelKey: 'insurance.ai.recommendationTypes.copilotSummary' }
];

export const UW_RULE_ACTION_OPTIONS = [
  { value: 'AutoApprove', labelKey: 'insurance.uw.ruleActions.autoApprove' },
  { value: 'AutoDecline', labelKey: 'insurance.uw.ruleActions.autoDecline' },
  { value: 'Refer', labelKey: 'insurance.uw.ruleActions.refer' },
  { value: 'RequireInfo', labelKey: 'insurance.uw.ruleActions.requireInfo' }
];

export type JsonArrayItem = Record<string, unknown>;
export type UwFormAnswerRow = { key: string; labelKey?: string; label: string; value: string };
export type AiExplanationFactorRow = { code: string; weight: string };

export function parseJsonArray(raw?: string | null): JsonArrayItem[] {
  if (!raw?.trim()) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .filter(item => item && typeof item === 'object' && !Array.isArray(item))
      .map(item => item as JsonArrayItem);
  } catch {
    return [];
  }
}

export function parseJsonObject(raw?: string | null): Record<string, unknown> {
  if (!raw?.trim()) {
    return {};
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // fall through
  }

  return {};
}

export function parsePartyIds(raw?: string | null): string[] {
  if (!raw?.trim()) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }

    const ids: string[] = [];
    for (const item of parsed) {
      if (typeof item === 'string' && item.trim()) {
        ids.push(item.trim());
        continue;
      }

      if (item && typeof item === 'object' && !Array.isArray(item)) {
        const partyId = (item as Record<string, unknown>)['partyId'];
        if (typeof partyId === 'string' && partyId.trim()) {
          ids.push(partyId.trim());
        }
      }
    }

    return ids;
  } catch {
    return [];
  }
}

export function buildUwFormAnswerFields(raw?: string | null): StructuredJsonFieldDef[] {
  const model = parseJsonObject(raw);
  return Object.entries(model)
    .filter(([, value]) => value !== null && value !== undefined && `${value}`.trim() !== '')
    .map(([key, value]) => ({
      key,
      type: resolveStructuredFieldType(value),
      labelKey: UW_FORM_ANSWER_FIELD_LABELS[key] ?? `insurance.uwCaseDetail.snapshot.answerKey.${key}`
    }));
}

export function buildSnapshotItemPayloads(
  raw: string | undefined | null,
  normalize?: (item: JsonArrayItem) => JsonArrayItem
): Array<{ index: number; payloadJson: string }> {
  return parseJsonArray(raw).map((item, index) => ({
    index,
    payloadJson: JSON.stringify(normalize ? normalize(item) : item)
  }));
}

export function buildPartyItemPayloads(raw?: string | null): Array<{ index: number; payloadJson: string }> {
  return parsePartyIds(raw).map((partyId, index) => ({
    index,
    payloadJson: JSON.stringify({ partyId })
  }));
}

function resolveStructuredFieldType(value: unknown): StructuredJsonFieldDef['type'] {
  if (typeof value === 'boolean') {
    return 'boolean';
  }
  if (typeof value === 'number') {
    return 'number';
  }
  return 'string';
}

export function buildUwFormAnswerRows(raw?: string | null): UwFormAnswerRow[] {
  const model = parseJsonObject(raw);
  return Object.entries(model)
    .filter(([, value]) => value !== null && value !== undefined && `${value}`.trim() !== '')
    .map(([key, value]) => ({
      key,
      labelKey: UW_FORM_ANSWER_FIELD_LABELS[key],
      label: humanizeFieldKey(key),
      value: formatDisplayValue(value)
    }));
}

export function buildAiExplanationFactorRows(raw?: string | null): AiExplanationFactorRow[] {
  const model = parseJsonObject(raw);
  const factors = model['factors'];
  if (!Array.isArray(factors)) {
    return [];
  }

  return factors
    .filter(item => item && typeof item === 'object' && !Array.isArray(item))
    .map(item => {
      const row = item as Record<string, unknown>;
      return {
        code: typeof row['code'] === 'string' ? row['code'] : '-',
        weight: formatDisplayValue(row['weight'] ?? '-')
      };
    });
}

export function buildAiExplanationRuleList(raw?: string | null): string[] {
  const model = parseJsonObject(raw);
  const rules = model['rules'];
  if (!Array.isArray(rules)) {
    return [];
  }

  return rules.map(item => `${item}`);
}

export function humanizeFieldKey(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^\w/, char => char.toUpperCase());
}

export function formatDisplayValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '-';
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (typeof value === 'number') {
    return `${value}`;
  }
  if (typeof value === 'string') {
    return value;
  }
  return JSON.stringify(value);
}

export function entityTypeLabelKey(entityType: string): string {
  return `insurance.ai.entityTypes.${entityType.charAt(0).toLowerCase()}${entityType.slice(1)}`;
}

export function recommendationTypeLabelKey(recommendationType: string): string {
  return `insurance.ai.recommendationTypes.${recommendationType.charAt(0).toLowerCase()}${recommendationType.slice(1)}`;
}

export function uwRuleActionLabelKey(action: string): string {
  return `insurance.uw.ruleActions.${action.charAt(0).toLowerCase()}${action.slice(1)}`;
}

export function uwDecisionLabelKey(decision: string): string {
  return `insurance.uw.decisions.${decision.charAt(0).toLowerCase()}${decision.slice(1)}`;
}

export function uwRoleLabelKey(role: string): string {
  const normalized = role.replace(/_/g, '');
  if (normalized === 'underwriter') {
    return 'insurance.uw.roles.underwriter';
  }
  if (normalized === 'seniorunderwriter') {
    return 'insurance.uw.roles.seniorUnderwriter';
  }
  if (normalized === 'underwritingmanager') {
    return 'insurance.uw.roles.underwritingManager';
  }
  return `insurance.uw.roles.${role}`;
}
